
import { useNavigate } from 'react-router-dom'
import type { Ingestion, ContentType, ProcessingStatus } from '@/types'
import { EmptyDataState } from '@/components/shared/EmptyState'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'

interface RecentIngestionsProps {
  ingestions: Ingestion[]
  onRefresh?: () => void
}

export default function RecentIngestions({ ingestions, onRefresh }: RecentIngestionsProps) {
  const navigate = useNavigate()

  const getContentTypeIcon = (type: ContentType) => {
    switch (type) {
      case 'audio': return '🎧'
      case 'video': return '🎬'
      case 'pdf': return '📄'
      case 'link': return '🔗'
      case 'image': return '🖼️'
      case 'conversation': return '💬'
      case 'text': return '📝'
      default: return '📁'
    }
  }

  const getStatusVariant = (status: ProcessingStatus) => {
    switch (status) {
      case 'completed': return 'text-emerald-400'
      case 'processing': return 'text-yellow-400'
      case 'pending': return 'text-blue-400'
      case 'failed': return 'text-red-400'
      default: return 'text-rsb-muted'
    }
  }

  if (ingestions.length === 0) {
    return (
      <div className="p-4">
        <h3 className="text-xs font-bold text-rsb-muted uppercase tracking-widest mb-4">Data Log</h3>
        <EmptyDataState onRefresh={onRefresh} />
      </div>
    )
  }

  return (
    <div className="bg-transparent">
      <div className="flex items-center justify-between px-4 pt-4 mb-2">
        <h3 className="text-xs font-bold text-rsb-muted uppercase tracking-widest">
           Data Stream
        </h3>
        <span className="text-[10px] font-mono text-rsb-muted">LIVE</span>
      </div>

      <ScrollArea className="h-[300px]">
        <table className="w-full text-left text-xs border-collapse">
          <thead>
            <tr className="border-b border-white/5 text-[10px] text-rsb-muted font-mono uppercase">
               <th className="px-4 py-2 font-normal">Type</th>
               <th className="px-4 py-2 font-normal">Content</th>
               <th className="px-4 py-2 font-normal">Status</th>
               <th className="px-4 py-2 font-normal text-right">Time</th>
            </tr>
          </thead>
          <tbody>
            {ingestions.map((ingestion) => (
              <tr
                key={ingestion.id}
                onClick={() => navigate(`/dive/${ingestion.id}`)}
                className="hover:bg-white/5 cursor-pointer group transition-colors border-b border-white/5 last:border-0"
              >
                <td className="px-4 py-3">
                  <span className="opacity-70 group-hover:opacity-100 transition-opacity">
                    {getContentTypeIcon(ingestion.content_type)}
                  </span>
                </td>
                <td className="px-4 py-3 max-w-[200px]">
                   <div className="truncate font-medium text-rsb-text group-hover:text-white transition-colors">
                      {ingestion.source_title || 'Untitled Data'}
                   </div>
                   <div className="text-[10px] text-rsb-muted truncate font-mono">
                      {ingestion.id.slice(0, 8)}
                   </div>
                </td>
                <td className="px-4 py-3">
                   <Badge
                     variant="outline"
                     className={`${getStatusVariant(ingestion.status)} border-current/20 text-[10px] uppercase font-bold tracking-wider`}
                   >
                     <div className={`w-1.5 h-1.5 rounded-full bg-current mr-1.5 ${ingestion.status === 'processing' ? 'animate-pulse' : ''}`} />
                     {ingestion.status}
                   </Badge>
                </td>
                <td className="px-4 py-3 text-right font-mono text-rsb-muted group-hover:text-rsb-text-secondary">
                   {new Date(ingestion.ingested_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollArea>
    </div>
  )
}
